import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiUpload, FiZap, FiLink, FiLock, FiRefreshCw } from 'react-icons/fi';
import tesoreriaService from '../services/tesoreriaService';
import conciliacionService from '../services/conciliacionService';
import Loading from '../components/Loading';
import './ConciliacionBancariaPage.css';

const ensureArray = (v) => Array.isArray(v) ? v : [];

const ConciliacionBancariaPage = () => {
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState('');
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [conciliacion, setConciliacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [movSistema, setMovSistema] = useState([]);
  const [movBanco, setMovBanco] = useState([]);
  const [selSistema, setSelSistema] = useState(null);
  const [selBanco, setSelBanco] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await tesoreriaService.cuentasBancarias.getAll({ activo: true });
        setCuentasBancarias(ensureArray(res.data.results || res.data));
      } catch { toast.error('Error al cargar cuentas'); }
      finally { setLoading(false); }
    })();
  }, []);

  const cargarConciliacion = async () => {
    if (!cuentaSeleccionada) return;
    try {
      setLoading(true);
      const res = await conciliacionService.getAll({ cuenta_bancaria: cuentaSeleccionada, mes, anio });
      const data = ensureArray(res.data.results || res.data);
      if (data.length > 0) {
        setConciliacion(data[0]);
        setMovSistema(data[0].movimientos_sistema || []);
        setMovBanco(data[0].movimientos_banco || []);
      } else {
        setConciliacion(null);
        setMovSistema([]);
        setMovBanco([]);
      }
    } catch { toast.error('Error al cargar conciliacion'); }
    finally { setLoading(false); }
  };

  const crearConciliacion = async () => {
    try {
      const res = await conciliacionService.create({ cuenta_bancaria: cuentaSeleccionada, mes, anio });
      setConciliacion(res.data);
      toast.success('Conciliacion creada');
      cargarConciliacion();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear conciliacion'); }
  };

  const handleImportar = async () => {
    if (!archivo || !conciliacion) return;
    const formData = new FormData();
    formData.append('archivo', archivo);
    try {
      await conciliacionService.importarCartola(conciliacion.id, formData);
      toast.success('Cartola importada exitosamente');
      setImportModalOpen(false);
      setArchivo(null);
      cargarConciliacion();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al importar cartola'); }
  };

  const handleConciliarAuto = async () => {
    if (!conciliacion) return;
    try {
      const res = await conciliacionService.conciliarAuto(conciliacion.id);
      toast.success(`Conciliacion automatica: ${res.data.conciliados || 0} partidas conciliadas`);
      cargarConciliacion();
    } catch { toast.error('Error en conciliacion automatica'); }
  };

  const handleConciliarManual = async () => {
    if (!selSistema || !selBanco) { toast.warn('Seleccione un movimiento del sistema y uno del banco'); return; }
    try {
      await conciliacionService.conciliarManual(conciliacion.id, {
        movimiento_cuenta_id: selSistema,
        movimiento_bancario_id: selBanco,
      });
      toast.success('Partidas conciliadas manualmente');
      setSelSistema(null);
      setSelBanco(null);
      cargarConciliacion();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al conciliar'); }
  };

  const handleCerrar = async () => {
    if (!window.confirm('¿Cerrar esta conciliacion?')) return;
    try {
      await conciliacionService.cerrar(conciliacion.id);
      toast.success('Conciliacion cerrada');
      cargarConciliacion();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al cerrar'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  if (loading && cuentasBancarias.length === 0) return <Loading />;

  return (
    <div className="conciliacion-page">
      <div className="page-header">
        <h1>Conciliacion Bancaria</h1>
      </div>

      {/* Selector */}
      <div className="selector-bar">
        <div className="form-group">
          <label>Cuenta Bancaria</label>
          <select value={cuentaSeleccionada} onChange={e => setCuentaSeleccionada(e.target.value)}>
            <option value="">Seleccionar cuenta...</option>
            {cuentasBancarias.map(c => <option key={c.id} value={c.id}>{c.nombre} - {c.banco}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Mes</label>
          <select value={mes} onChange={e => setMes(parseInt(e.target.value))}>
            {[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>{['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'][i]}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Ano</label>
          <select value={anio} onChange={e => setAnio(parseInt(e.target.value))}>
            {[2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <button className="btn-primary" onClick={cargarConciliacion} disabled={!cuentaSeleccionada}>
          <FiRefreshCw /> Cargar
        </button>
      </div>

      {!conciliacion && cuentaSeleccionada && !loading && (
        <div className="no-conciliacion">
          <p>No existe conciliacion para este periodo.</p>
          <button className="btn-primary" onClick={crearConciliacion}>Crear Conciliacion</button>
        </div>
      )}

      {conciliacion && (
        <>
          {/* Resumen */}
          <div className="resumen-conciliacion">
            <div className="resumen-item">
              <span>Saldo Libro</span>
              <strong>{formatCurrency(conciliacion.saldo_libro)}</strong>
            </div>
            <div className="resumen-item">
              <span>Saldo Banco</span>
              <strong>{formatCurrency(conciliacion.saldo_banco)}</strong>
            </div>
            <div className="resumen-item" style={{ color: conciliacion.diferencia == 0 ? '#059669' : '#dc2626' }}>
              <span>Diferencia</span>
              <strong>{formatCurrency(conciliacion.diferencia)}</strong>
            </div>
            <div className="resumen-item">
              <span>Estado</span>
              <span className={`badge ${conciliacion.estado === 'cerrada' ? 'badge-green' : 'badge-yellow'}`}>{conciliacion.estado}</span>
            </div>
          </div>

          {/* Acciones */}
          {conciliacion.estado !== 'cerrada' && (
            <div className="acciones-bar">
              <button className="btn-secondary" onClick={() => setImportModalOpen(true)}><FiUpload /> Importar Cartola CSV</button>
              <button className="btn-primary" onClick={handleConciliarAuto}><FiZap /> Conciliar Automatico</button>
              <button className="btn-primary" onClick={handleConciliarManual} disabled={!selSistema || !selBanco}><FiLink /> Conciliar Manual</button>
              <button className="btn-success" onClick={handleCerrar}><FiLock /> Cerrar Conciliacion</button>
            </div>
          )}

          {/* Vista lado a lado */}
          <div className="lado-a-lado">
            <div className="lado-panel">
              <h3>Movimientos Sistema</h3>
              <table className="data-table">
                <thead><tr><th></th><th>Fecha</th><th>Concepto</th><th style={{ textAlign: 'right' }}>Monto</th><th>Estado</th></tr></thead>
                <tbody>
                  {movSistema.length === 0 ? <tr><td colSpan="5" className="text-muted" style={{ textAlign: 'center', padding: 20 }}>Sin movimientos</td></tr> :
                  movSistema.map((m, i) => (
                    <tr key={i} className={selSistema === m.id ? 'selected' : ''} onClick={() => !m.conciliado && setSelSistema(m.id)} style={{ cursor: m.conciliado ? 'default' : 'pointer' }}>
                      <td><input type="radio" checked={selSistema === m.id} onChange={() => {}} disabled={m.conciliado} /></td>
                      <td>{m.fecha}</td>
                      <td>{m.concepto}</td>
                      <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace" }}>{formatCurrency(m.monto)}</td>
                      <td><span className={`badge ${m.conciliado ? 'badge-green' : 'badge-yellow'}`}>{m.conciliado ? 'Conciliado' : 'Pendiente'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="lado-panel">
              <h3>Movimientos Banco (Cartola)</h3>
              <table className="data-table">
                <thead><tr><th></th><th>Fecha</th><th>Descripcion</th><th style={{ textAlign: 'right' }}>Monto</th><th>Estado</th></tr></thead>
                <tbody>
                  {movBanco.length === 0 ? <tr><td colSpan="5" className="text-muted" style={{ textAlign: 'center', padding: 20 }}>Sin movimientos importados</td></tr> :
                  movBanco.map((m, i) => (
                    <tr key={i} className={selBanco === m.id ? 'selected' : ''} onClick={() => !m.conciliado && setSelBanco(m.id)} style={{ cursor: m.conciliado ? 'default' : 'pointer' }}>
                      <td><input type="radio" checked={selBanco === m.id} onChange={() => {}} disabled={m.conciliado} /></td>
                      <td>{m.fecha}</td>
                      <td>{m.descripcion}</td>
                      <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace" }}>{formatCurrency(m.monto)}</td>
                      <td><span className={`badge ${m.conciliado ? 'badge-green' : 'badge-yellow'}`}>{m.conciliado ? 'Conciliado' : 'Pendiente'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modal Importar */}
      {importModalOpen && (
        <div className="modal-overlay" onClick={() => setImportModalOpen(false)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <h2>Importar Cartola Bancaria</h2>
            <div className="upload-zone" onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); setArchivo(e.dataTransfer.files[0]); }}>
              <FiUpload size={32} />
              <p>Arrastra un archivo CSV aqui o</p>
              <input type="file" accept=".csv,.xls,.xlsx" onChange={e => setArchivo(e.target.files[0])} />
              {archivo && <p className="file-name">{archivo.name}</p>}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setImportModalOpen(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleImportar} disabled={!archivo}>Importar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConciliacionBancariaPage;
